import { useState, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, Loader2 } from 'lucide-react';
import { stripeApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { estFormuleRetenue } from '@/lib/formule';
import { OFFRE_COMPLETE } from '@/lib/tarifs';
import { RenonciationRetractation } from '@/components/RenonciationRetractation';
import { TarifRetenue } from '@/components/TarifRetenue';

interface PricingModalProps {
    onClose: () => void;
}

export function PricingModal({ onClose }: PricingModalProps): ReactNode {
    const { user } = useAuth();
    const formuleRetenue = estFormuleRetenue(user);
    const [loading, setLoading] = useState<'monthly' | 'annual' | null>(null);
    const [error, setError] = useState('');
    const [waiverAccepted, setWaiverAccepted] = useState(false);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const handleCheckout = async (plan: 'monthly' | 'annual') => {
        if (!waiverAccepted) {
            setError('Vous devez accepter la renonciation au droit de rétractation pour souscrire.');
            return;
        }
        setLoading(plan);
        setError('');
        try {
            const { url } = await stripeApi.createCheckoutSession(plan, true);
            if (url) {
                window.location.href = url;
            }
        } catch (err: unknown) {
            const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                || 'Une erreur est survenue. Réessayez ou contactez-nous à contact@btpal.fr.';
            setError(message);
            setLoading(null);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm overflow-y-auto overscroll-contain" onClick={onClose}>
            <div className="min-h-full flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl overflow-hidden my-4" onClick={e => e.stopPropagation()}>
                <div className="p-6 sm:p-8">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-xl font-bold text-[#1E293B]">
                                {formuleRetenue ? 'Votre formule à l\'année' : 'Choisissez votre offre'}
                            </h2>
                            <p className="text-sm text-[#64748B] mt-1">
                                {formuleRetenue
                                    ? 'Le suivi de vos retenues de garantie, payé une fois par an.'
                                    : 'Toutes les fonctionnalités incluses, sans limite.'}
                            </p>
                        </div>
                        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-[#94A3B8] hover:text-[#1E293B] transition-colors">
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {formuleRetenue ? (
                        <TarifRetenue />
                    ) : (
                    <>
                        {error && (
                            <div className="mb-4 p-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg text-sm text-[#991B1B]">
                                {error}
                            </div>
                        )}

                        {/* Pricing cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                            {/* Mensuel */}
                            <div className="border border-[#E2E8F0] rounded-lg p-4 flex flex-col">
                                <p className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-2">Mensuel</p>
                                <div className="mb-0.5">
                                    <span className="text-2xl font-bold text-[#1E293B]">{OFFRE_COMPLETE.mensuel}€</span>
                                    <span className="text-xs text-[#64748B]">/mois</span>
                                </div>
                                <p className="text-xs text-[#94A3B8] mb-4">Sans engagement</p>
                                <button
                                    onClick={() => handleCheckout('monthly')}
                                    disabled={loading !== null || !waiverAccepted}
                                    className="mt-auto w-full py-2 rounded-lg border border-[#0F2744] text-[#1D4ED8] text-sm font-semibold hover:bg-[#EFF6FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {loading === 'monthly' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Choisir'}
                                </button>
                            </div>

                            {/* Annuel */}
                            <div className="border-2 border-[#0F2744] rounded-lg p-4 relative bg-[#EFF6FF] flex flex-col">
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0F2744] text-white text-[10px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
                                    RECOMMANDÉ
                                </div>
                                <p className="text-[10px] font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">Annuel</p>
                                <div className="mb-0.5">
                                    <span className="text-2xl font-bold text-[#1E293B]">{OFFRE_COMPLETE.annuelParMois}€</span>
                                    <span className="text-xs text-[#64748B]">/mois</span>
                                </div>
                                <p className="text-xs text-[#10B981] font-semibold mb-0.5">soit {OFFRE_COMPLETE.annuel}€/an (2 mois offerts)</p>
                                <p className="text-xs text-[#94A3B8] mb-4">Économisez {OFFRE_COMPLETE.economieAnnuelle} €/an</p>
                                <button
                                    onClick={() => handleCheckout('annual')}
                                    disabled={loading !== null || !waiverAccepted}
                                    className="mt-auto w-full py-2 rounded-lg bg-[#0F2744] text-white text-sm font-semibold hover:bg-[#1A3A5C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {loading === 'annual' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Choisir'}
                                </button>
                            </div>

                            {/* Entreprise */}
                            <div className="border border-[#E2E8F0] rounded-lg p-4 flex flex-col">
                                <p className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-2">Entreprise</p>
                                <div className="mb-0.5">
                                    <span className="text-xl font-bold text-[#1E293B]">Sur devis</span>
                                </div>
                                <p className="text-xs text-[#94A3B8] mb-4">Serveur isolé · SLA · Support dédié</p>
                                <button
                                    onClick={() => window.open('mailto:contact@btpal.fr?subject=Offre%20Entreprise%20BTPal', '_blank')}
                                    disabled={loading !== null}
                                    className="mt-auto w-full py-2 rounded-lg border border-[#E2E8F0] text-[#64748B] text-sm font-semibold hover:border-[#CBD5E1] hover:text-[#1E293B] transition-colors disabled:opacity-50"
                                >
                                    Nous contacter
                                </button>
                            </div>
                        </div>

                        <p className="text-[11px] text-[#64748B] mb-4 px-1">TVA non applicable (article 293 B du CGI).</p>

                        <RenonciationRetractation
                            acceptee={waiverAccepted}
                            onChange={(acceptee) => {
                                setWaiverAccepted(acceptee);
                                if (acceptee) setError('');
                            }}
                        />

                        <p className="text-[11px] text-[#64748B] mb-4 -mt-2 px-1">
                            Résiliable à tout moment depuis votre compte. L'accès reste actif jusqu'à la fin de la période déjà payée.
                        </p>

                        {/* Features */}
                        <div className="bg-[#F8FAFC] rounded-lg px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
                            {[
                                'Factures & devis illimites',
                                'Gestion des chantiers',
                                'Prevision de tresorerie',
                                'Export comptable FEC',
                                'Situations de travaux',
                                'Support par email',
                            ].map(f => (
                                <div key={f} className="flex items-center gap-1.5 text-xs text-[#374151]">
                                    <CheckCircle className="h-3.5 w-3.5 text-[#10B981] shrink-0" />
                                    <span>{f}</span>
                                </div>
                            ))}
                        </div>
                    </>
                    )}
                </div>
            </div>
            </div>
        </div>,
        document.body
    );
}
